import { useStore } from "@nanostores/react";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { dataStore, filteredValuesStore, setTypeValues, updateUrl } from "../../_utils/searchStore";
import type { Value } from "../../_utils/types";
import { Checkbox } from "./Checkbox";
import { Collapse } from "./Collapse";
import { MultiSelect } from "./MultiSelect";
import { drawerTypeStore } from "./store";

const ALL_DURATIONS = [
  { label: "Court séjour - 1 à 4 jours", value: "WEEKEND" },
  { label: "1 semaine", value: "BREAK" },
  { label: "Plus d'une semaine", value: "LONG" },
];

type Props = {
  isDesktop?: boolean;
  fit?: boolean;
};
export const Duration = ({ isDesktop = false, fit = false }: Props) => {
  const router = useRouter();
  const state = useStore(drawerTypeStore);
  const selectedDurations = useStore(filteredValuesStore("durations"));
  const [localDurations, setLocalDurations] = useState(selectedDurations);
  const selectedDurationsRef = useRef(selectedDurations);
  const data = useStore(dataStore);

  useEffect(() => {
    const isDiff = () => {
      const oldT = selectedDurationsRef.current.sort((a, b) => a.value.localeCompare(b.value));

      const newT = selectedDurations.sort((a, b) => a.value.localeCompare(b.value));

      if (oldT.length !== newT.length) return true;

      for (let index = 0; index < newT.length; index++) {
        if (oldT[index] !== newT[index]) return true;
      }

      return false;
    };

    if (isDiff()) {
      selectedDurationsRef.current = selectedDurations;
      setLocalDurations(selectedDurations);
    }
  }, [selectedDurations]);

  const handleChange = (newValue: Value, directUpdate = false) => {
    let newDurations: Array<Value> = [];
    if (localDurations.some((lt) => lt.value === newValue.value)) {
      newDurations = localDurations.filter((lt) => lt.value !== newValue.value);
    } else {
      newDurations = [...localDurations, newValue];
    }

    setLocalDurations(newDurations);

    if (directUpdate) {
      setTypeValues("durations", newDurations);
      updateUrl(router);
    }
  };

  const handleOpen = () => {
    drawerTypeStore.set(state === "duration" ? null : "duration");
  };

  const handleClickOutside = () => {
    setTypeValues("durations", localDurations);

    drawerTypeStore.set(null);
  };

  const handleValidate = () => {
    setTypeValues("durations", localDurations);
    updateUrl(router);
  };

  const handleClear = () => {
    setTypeValues("durations", []);
    updateUrl(router);
  };

  const durations = [
    ...selectedDurations
      .filter((s) => !data?.durations?.some((t) => t.key === s.value))
      .map((s) => ({
        value: s.value,
      })),
    ...(data?.durations?.map((t) => ({ value: t.key })) || []),
  ];

  return isDesktop ? (
    <MultiSelect
      fit={fit}
      placeholder="Durée"
      type="durations"
      onChange={handleChange}
      onValidate={handleValidate}
      onClear={handleClear}
      localValues={localDurations}
      selectedValues={selectedDurations}
      values={ALL_DURATIONS.filter((b) => durations.some((duration) => duration.value === b.value))}
      disableSort
      disableSearch
    />
  ) : (
    <Collapse
      open={state === "duration"}
      title="Durée"
      onclick={handleOpen}
      onClickOutside={handleClickOutside}
      count={selectedDurations.length}
    >
      <div className="flex flex-col max-lg:space-y-3 mt-5 lg:flex-row lg:space-x-5">
        {ALL_DURATIONS.filter((b) => durations.some((duration) => duration.value === b.value)).map((duration) => (
          <Checkbox
            key={duration.value}
            label={duration.label}
            checked={localDurations.some((lt) => lt.value === duration.value)}
            onChange={() =>
              handleChange({
                type: "durations",
                value: duration.value,
              })
            }
          />
        ))}
      </div>
    </Collapse>
  );
};
